"use client";

import { Link } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  Calculator,
  Compass,
  GraduationCap,
  Package,
} from "lucide-react";
import { NavLink } from "./primitives/nav-link";
import { Account } from "@/components/account";
import { ChangelogMenu } from "@/components/changelog-menu";
import { ThemeToggle } from "./theme-toggle";
import type { ChangelogEntry } from "@/lib/content/changelog";
import { useSession } from "@/lib/auth-client";
import { useIsClient } from "@/lib/hooks/useIsClient";
import logoSmallUrl from "@/assets/logo-small.svg?url";

const NAV_ITEMS: Array<{
  href: string;
  label: string;
  icon: LucideIcon;
}> = [
  { href: "/calc", label: "Calculator", icon: Calculator },
  { href: "/recipes", label: "Recipes", icon: BookOpen },
  { href: "/ingredients", label: "Ingredients", icon: Package },
  { href: "/discover", label: "Discover", icon: Compass },
  { href: "/learn", label: "Learn", icon: GraduationCap },
];

function NavActions({
  recentChangelog,
}: {
  recentChangelog: ChangelogEntry[];
}) {
  const isClient = useIsClient();
  const clientSession = useSession();

  // Hide everything until we know what to show here for Account
  if (!isClient || clientSession.isPending) {
    return null;
  }

  return (
    <>
      <div className="flex items-center">
        <ChangelogMenu entries={recentChangelog} />
        <ThemeToggle />
      </div>
      <Account />
    </>
  );
}

export function Navigation({
  recentChangelog,
}: {
  recentChangelog: ChangelogEntry[];
}) {
  return (
    <nav className="bg-card border-b print:hidden">
      <div className="container mx-auto flex h-14 items-center justify-between px-4 py-2">
        <Link
          to="/"
          className="text-foreground flex items-center gap-2 text-xl font-bold"
        >
          <img
            src={logoSmallUrl}
            alt="Scoopulator Logo"
            width={34}
            height={34}
          />
          <span className="tracking-tight lg:hidden xl:block">Scoopulator</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="absolute left-1/2 hidden -translate-x-1/2 items-center lg:flex">
          {NAV_ITEMS.map(({ href, label, icon: Icon }) => (
            <NavLink key={href} href={href}>
              <span className="flex items-center gap-2">
                <Icon aria-hidden="true" />
                <span>{label}</span>
              </span>
            </NavLink>
          ))}
        </div>

        <div className="hidden items-center gap-1 md:flex">
          <NavActions recentChangelog={recentChangelog} />
        </div>

        {/* Mobile Navigation */}
        <div className="flex items-center space-x-1 md:hidden">
          <NavActions recentChangelog={recentChangelog} />
        </div>
      </div>

      {/* Mobile secondary nav row */}
      <div className="overflow-x-auto lg:hidden">
        <div className="grid grid-cols-5 items-center gap-1 px-1 py-1 sm:mx-auto sm:flex sm:w-fit sm:justify-center sm:gap-6">
          {NAV_ITEMS.map(({ href, label, icon: Icon }) => (
            <NavLink key={href} href={href} size="sm">
              <span className="flex flex-col items-center gap-1 text-center">
                <Icon aria-hidden="true" />
                <span>{label}</span>
              </span>
            </NavLink>
          ))}
        </div>
      </div>
    </nav>
  );
}
